import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

/**
 * Live-progress stepper card for the KuBIT sync operation.
 *
 * Renders a fixed 13-row layout — countries → cities → unit_measures →
 * entity_types → phone_prefixes → branches → entities → items → item_prices →
 * default_supplier_bootstrap → reconcile_suppliers → reconcile_entities →
 * reconcile_items — with each row showing one of five states:
 *   - pending  (○ idle gray, no started_at yet)
 *   - running  (⟳ animated spinner, navy text)
 *   - success  (✓ green, with final counters + duration)
 *   - error    (⚠ red, with the failure message)
 *   - skipped  (— muted, with skip reason on hover)
 *
 * The component is fed entirely from props — it doesn't fetch on its own.
 * The parent (KubitIntegration) owns the polling cycle and dispatches
 * getKubitSyncProgress; this component just renders whatever state Redux
 * surfaced. That separation lets the same component double as the read-only
 * detail view inside the run-history list.
 *
 * Auto-collapse behavior: when a run reaches a terminal state (success /
 * partial / error / aborted) and `allowAutoCollapse` is true, after 30s the
 * card collapses to a one-line summary; clicking "Trego detajet" re-expands.
 * The history-list usage of this component disables auto-collapse so the
 * detail view stays open until the user closes it.
 */

type StepStatus = 'pending' | 'running' | 'success' | 'error' | 'skipped';

interface SyncStep {
    step_key: string;
    step_order: number;
    status: StepStatus;
    started_at: string | null;
    finished_at: string | null;
    duration_ms: number | null;
    payload: any;
    message: string | null;
}

interface SyncRun {
    run_uuid: string;
    status: 'running' | 'success' | 'partial' | 'error' | 'aborted';
    triggered_by: string;
    resource_filter: string | null;
    started_at: string | null;
    finished_at: string | null;
    total_duration_ms: number | null;
    error_message: string | null;
}

interface Props {
    run: SyncRun | null;
    steps: SyncStep[];
    allowAutoCollapse?: boolean;
    autoCollapseAfterMs?: number;
}

const STEP_KEYS_ORDER = [
    'countries',
    'cities',
    'unit_measures',
    'entity_types',
    'phone_prefixes',
    'branches',
    'entities',
    'items',
    'item_prices',
    'default_supplier_bootstrap',
    'reconcile_suppliers',
    'reconcile_entities',
    'reconcile_items',
];

const TERMINAL_STATUSES = ['success', 'partial', 'error', 'aborted'];

const KubitSyncProgressCard: React.FC<Props> = ({
    run,
    steps,
    allowAutoCollapse = true,
    autoCollapseAfterMs = 30_000,
}) => {
    const intl = useIntl();
    const [forcedCollapsed, setForcedCollapsed] = useState(false);
    const [manuallyExpanded, setManuallyExpanded] = useState(false);

    const isTerminal = run !== null && TERMINAL_STATUSES.includes(run.status);

    // Auto-collapse timer: kick off when the run first hits a terminal state.
    // Reset whenever the run_uuid changes (a new sync starts), so the user
    // sees the next live run fully expanded by default.
    useEffect(() => {
        if (!allowAutoCollapse || !isTerminal || run === null) {
            return;
        }
        const timer = window.setTimeout(() => setForcedCollapsed(true), autoCollapseAfterMs);
        return () => window.clearTimeout(timer);
    }, [allowAutoCollapse, isTerminal, run?.run_uuid, autoCollapseAfterMs]);

    useEffect(() => {
        // New run started — reset the collapse state so the next sync's
        // stepper renders fully expanded again.
        setForcedCollapsed(false);
        setManuallyExpanded(false);
    }, [run?.run_uuid]);

    // Stable order: pad missing step rows with synthetic pending entries so
    // the layout never flickers if the polling endpoint returns a partial
    // list (e.g. mid-insert race window during a sync's first 100ms).
    const orderedSteps: SyncStep[] = useMemo(() => {
        const byKey = new Map<string, SyncStep>();
        steps.forEach((s) => byKey.set(s.step_key, s));
        return STEP_KEYS_ORDER.map((key, idx) => {
            const real = byKey.get(key);
            if (real) return real;
            return {
                step_key: key,
                step_order: idx + 1,
                status: 'pending' as StepStatus,
                started_at: null,
                finished_at: null,
                duration_ms: null,
                payload: null,
                message: null,
            };
        });
    }, [steps]);

    // ─── Minimum visible "running" duration per step ────────────────────
    // Fast steps (<MIN_RUNNING_MS backend-side) flip pending → success
    // between polling ticks, robbing the user of any sense that the step
    // actually ran. Once we observe a step in 'running' state at least
    // once, we hold that visual state for MIN_RUNNING_MS even if the very
    // next poll says it's already finished. Counters and final duration
    // catch up the moment we release the hold. NOT used during pending
    // → skipped or for steps we never saw running.
    const MIN_RUNNING_MS = 700;

    // Per-step timestamps of when we first saw them as 'running'. Stored
    // in a ref to survive re-renders without retriggering effects.
    const firstSeenRunningRef = useRef<{ [stepKey: string]: number }>({});

    // Bumps when a held step's timer expires, forcing a re-render so the
    // computed status drops back to whatever the backend now says.
    const [, setTick] = useState(0);

    const displaySteps: SyncStep[] = useMemo(() => {
        const now = Date.now();
        return orderedSteps.map((step) => {
            const seen = firstSeenRunningRef.current;

            if (step.status === 'running' && !seen[step.step_key]) {
                seen[step.step_key] = now;
            }

            // If the backend already says success/error but we held it as
            // running, decide whether to keep showing it as running.
            if (
                (step.status === 'success' || step.status === 'error') &&
                seen[step.step_key]
            ) {
                const elapsed = now - seen[step.step_key];
                if (elapsed < MIN_RUNNING_MS) {
                    // Schedule a re-render at the moment the hold expires.
                    const remaining = MIN_RUNNING_MS - elapsed;
                    setTimeout(() => setTick((t) => t + 1), remaining);
                    return { ...step, status: 'running' as StepStatus };
                }
            }
            return step;
        });
    }, [orderedSteps]);

    // Reset the seen-running tracking whenever a brand new run starts.
    useEffect(() => {
        firstSeenRunningRef.current = {};
    }, [run?.run_uuid]);

    if (run === null) {
        return null;
    }

    const collapsed = forcedCollapsed && !manuallyExpanded;
    const cardVariant = `is-${run.status}`;

    // Header is click-to-toggle only when the run is in a terminal state.
    // During an active sync, keeping the header static prevents an
    // accidental dismiss of the live progress mid-flight. Once finished,
    // user can collapse / re-expand freely (the 30s auto-collapse still
    // fires for users who walk away).
    const headerClickable = isTerminal;

    function toggleCollapse() {
        if (!headerClickable) return;
        if (collapsed) {
            setManuallyExpanded(true);
            setForcedCollapsed(false);
        } else {
            setForcedCollapsed(true);
            setManuallyExpanded(false);
        }
    }

    return (
        <div className={`kubit-stepper-card ${cardVariant}${headerClickable ? ' is-collapsible' : ''}`}>
            <div
                className="kubit-stepper-header"
                onClick={toggleCollapse}
                role={headerClickable ? 'button' : undefined}
                tabIndex={headerClickable ? 0 : undefined}
                onKeyDown={(e) => {
                    if (!headerClickable) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleCollapse();
                    }
                }}
                aria-expanded={headerClickable ? !collapsed : undefined}
            >
                <span className="kubit-stepper-header-title">
                    {renderHeaderTitle(run)}
                </span>
                <span className="kubit-stepper-header-right">
                    {run.total_duration_ms !== null && (
                        <span className="kubit-stepper-header-duration">
                            {formatDuration(run.total_duration_ms)}
                        </span>
                    )}
                    {headerClickable && (
                        <span className="kubit-stepper-header-chevron" aria-hidden="true">
                            {collapsed ? '▸' : '▾'}
                        </span>
                    )}
                </span>
            </div>

            {collapsed ? (
                <div className="kubit-stepper-collapsed">
                    <span className="kubit-stepper-collapsed-summary">
                        {renderCollapsedSummary(run, orderedSteps, intl)}
                    </span>
                    <button
                        type="button"
                        className="kubit-stepper-expand-btn"
                        onClick={() => setManuallyExpanded(true)}
                    >
                        <FormattedMessage
                            id="integrations.kubit.stepper.showDetails"
                            defaultMessage="Show details"
                        />
                    </button>
                </div>
            ) : (
                <ul className="kubit-stepper-rows">
                    {displaySteps.map((step) => (
                        <StepRow key={step.step_key} step={step} />
                    ))}
                </ul>
            )}
        </div>
    );
};

interface StepRowProps {
    step: SyncStep;
}

const StepRow: React.FC<StepRowProps> = ({ step }) => {
    const intl = useIntl();
    const labelId = `integrations.kubit.stepper.steps.${step.step_key}.${step.status === 'running' ? 'active' : 'label'}`;

    return (
        <li className={`kubit-stepper-row is-${step.status}`}>
            <span className="kubit-stepper-row-icon" aria-hidden="true">
                {renderIcon(step.status)}
            </span>
            <span className="kubit-stepper-row-label">
                <FormattedMessage
                    id={labelId}
                    defaultMessage={step.step_key}
                />
            </span>
            <span className="kubit-stepper-row-meta">
                {renderRowMeta(step, intl)}
            </span>
            {step.status === 'error' && step.message && (
                <span className="kubit-stepper-row-error" title={step.message}>
                    {step.message.slice(0, 80)}{step.message.length > 80 ? '…' : ''}
                </span>
            )}
        </li>
    );
};

// ─── render helpers ────────────────────────────────────────────────────────

function renderIcon(status: StepStatus): React.ReactNode {
    switch (status) {
        case 'pending':
            return <span className="kubit-icon-pending">○</span>;
        case 'running':
            return <KubitSpinner />;
        case 'success':
            return <span className="kubit-icon-success">✓</span>;
        case 'error':
            return <span className="kubit-icon-error">⚠</span>;
        case 'skipped':
            return <span className="kubit-icon-skipped">—</span>;
        default:
            return null;
    }
}

const KubitSpinner: React.FC = () => (
    <svg className="kubit-spinner" width={14} height={14} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
        <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
);

function renderHeaderTitle(run: SyncRun): React.ReactNode {
    const map: { [k: string]: string } = {
        running: 'integrations.kubit.stepper.title.running',
        success: 'integrations.kubit.stepper.title.success',
        partial: 'integrations.kubit.stepper.title.partial',
        error: 'integrations.kubit.stepper.title.error',
        aborted: 'integrations.kubit.stepper.title.aborted',
    };
    const id = map[run.status] || map.running;
    return (
        <FormattedMessage
            id={id}
            defaultMessage={run.status === 'running' ? 'Sync in progress' : 'Sync ' + run.status}
        />
    );
}

function renderRowMeta(step: SyncStep, intl: any): React.ReactNode {
    if (step.status === 'pending') {
        return (
            <FormattedMessage
                id="integrations.kubit.stepper.status.pending"
                defaultMessage="Pending"
            />
        );
    }

    const payload = step.payload || {};
    const isPuller = [
        'countries', 'cities', 'unit_measures', 'entity_types', 'phone_prefixes',
        'branches', 'entities', 'items', 'item_prices',
    ].includes(step.step_key);
    const isReconciler = step.step_key.startsWith('reconcile_');

    const parts: React.ReactNode[] = [];

    if (isPuller && typeof payload.rows_pulled === 'number') {
        parts.push(
            <span key="rows" className="kubit-stepper-counter">
                <FormattedMessage
                    id="integrations.kubit.stepper.counter.rowsPulled"
                    defaultMessage="{count} rows"
                    values={{ count: payload.rows_pulled }}
                />
            </span>
        );
    }

    if (isReconciler) {
        if (typeof payload.linked === 'number' && payload.linked > 0) {
            parts.push(
                <span key="linked" className="kubit-stepper-counter">
                    <FormattedMessage
                        id="integrations.kubit.stepper.counter.linked"
                        defaultMessage="{count} linked"
                        values={{ count: payload.linked }}
                    />
                </span>
            );
        }
        if (typeof payload.created === 'number' && payload.created > 0) {
            parts.push(
                <span key="created" className="kubit-stepper-counter">
                    <FormattedMessage
                        id="integrations.kubit.stepper.counter.created"
                        defaultMessage="{count} created"
                        values={{ count: payload.created }}
                    />
                </span>
            );
        }
        if (typeof payload.potential_duplicate === 'number' && payload.potential_duplicate > 0) {
            parts.push(
                <span key="potential_duplicate" className="kubit-stepper-counter">
                    <FormattedMessage
                        id="integrations.kubit.stepper.counter.potentialDuplicate"
                        defaultMessage="{count} potential duplicate"
                        values={{ count: payload.potential_duplicate }}
                    />
                </span>
            );
        }
    }

    if (step.duration_ms !== null && step.status !== 'running') {
        parts.push(
            <span key="duration" className="kubit-stepper-duration">
                {formatDuration(step.duration_ms)}
            </span>
        );
    }

    if (step.status === 'running' && step.started_at) {
        parts.push(
            <span key="elapsed" className="kubit-stepper-duration">
                {formatElapsedSince(step.started_at)}
            </span>
        );
    }

    if (parts.length === 0) {
        return (
            <FormattedMessage
                id={`integrations.kubit.stepper.status.${step.status}`}
                defaultMessage={step.status}
            />
        );
    }

    return <>{parts}</>;
}

function renderCollapsedSummary(run: SyncRun, steps: SyncStep[], intl: any): React.ReactNode {
    const linked = steps.reduce((sum, s) => {
        const p = s.payload || {};
        return sum + (typeof p.linked === 'number' ? p.linked : 0);
    }, 0);
    const created = steps.reduce((sum, s) => {
        const p = s.payload || {};
        return sum + (typeof p.created === 'number' ? p.created : 0);
    }, 0);
    const rows = steps.reduce((sum, s) => {
        const p = s.payload || {};
        return sum + (typeof p.rows_pulled === 'number' ? p.rows_pulled : 0);
    }, 0);
    return (
        <FormattedMessage
            id={`integrations.kubit.stepper.collapsed.${run.status}`}
            defaultMessage="Sync {status}: {rows} rows, {linked} linked, {created} created ({duration})"
            values={{
                status: run.status,
                rows,
                linked,
                created,
                duration: run.total_duration_ms !== null ? formatDuration(run.total_duration_ms) : '?',
            }}
        />
    );
}

function formatDuration(ms: number): string {
    if (ms < 1000) return `${ms} ms`;
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)} s`;
    const minutes = Math.floor(seconds / 60);
    const rem = Math.round(seconds % 60);
    return `${minutes}m ${rem}s`;
}

function formatElapsedSince(iso: string): string {
    const start = new Date(iso).getTime();
    const elapsedMs = Date.now() - start;
    return formatDuration(elapsedMs);
}

export default KubitSyncProgressCard;
